import type { ComponentType, ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Construction, Store } from "lucide-react";
import { useSession } from "@/stores/session";
import { useActiveOrg, useModules } from "@/features/tenancy/hooks";
import { useModuleAccess } from "@/features/plan/hooks";
import { LockedModule } from "@/features/plan/Locked";
import { AppShell } from "@/layouts/AppShell";
import { LoginPage } from "@/features/auth/LoginPage";
import { OrdersPage } from "@/features/orders/OrdersPage";
import { OrderDetailPage } from "@/features/orders/OrderDetailPage";
import { InventoryPage } from "@/features/inventory/InventoryPage";
import { ProductsPage } from "@/features/catalog/ProductsPage";
import { ProductDetailPage } from "@/features/catalog/ProductDetailPage";
import { NewProductPage } from "@/features/catalog/NewProductPage";
import { CustomersPage } from "@/features/catalog/CustomersPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { AttributesPage } from "@/features/settings/AttributesPage";
import { CategoriesPage } from "@/features/settings/CategoriesPage";
import { BrandsPage } from "@/features/settings/BrandsPage";
import { IdentityPage } from "@/features/settings/IdentityPage";
import { CommercialPage } from "@/features/settings/CommercialPage";
import { DeliveryPage } from "@/features/settings/DeliveryPage";
import { ShippingPage } from "@/features/shipping/ShippingPage";
import { PaymentsPage } from "@/features/payments/PaymentsPage";
import { MarketingPage } from "@/features/marketing/MarketingPage";
import { PlanPage } from "@/features/plan/PlanPage";
import { Toaster } from "@/components/ui/toast";
import { EmptyState, Skeleton } from "@/components/ui/states";
import { NAV } from "./navigation";

/** Modulo a pantalla. Lo que no esta aqui todavia muestra un aviso honesto. */
const SCREENS: Record<string, ComponentType> = {
  dashboard: DashboardPage,
  orders: OrdersPage,
  catalog: ProductsPage,
  inventory: InventoryPage,
  customers: CustomersPage,
  shipping: ShippingPage,
  payments: PaymentsPage,
  marketing: MarketingPage,
};

export function Router() {
  const status = useSession((s) => s.status);

  if (status === "loading") return <Splash />;
  if (status === "anonymous") return <LoginPage />;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={<HomeRedirect />} />

          <Route
            path="/ventas/:id"
            element={
              <ModuleGuard module="orders" title="Ventas">
                <OrderDetailPage />
              </ModuleGuard>
            }
          />
          {/* Antes que /catalogo/:id. React Router ya prioriza el segmento
              fijo sobre el dinamico, pero el orden lo deja explicito: si
              alguien renombra la ruta, el conflicto se ve aqui. */}
          <Route
            path="/catalogo/nuevo"
            element={
              <ModuleGuard module="catalog" title="Catálogo">
                <NewProductPage />
              </ModuleGuard>
            }
          />

          <Route
            path="/catalogo/:id"
            element={
              <ModuleGuard module="catalog" title="Catálogo">
                <ProductDetailPage />
              </ModuleGuard>
            }
          />

          {/* Los atributos se administran en Configuracion, no dentro de un
              producto: son del catalogo entero y su numero depende del plan. */}
          <Route
            path="/configuracion"
            element={
              <ModuleGuard module="store_settings" title="Configuración">
                <SettingsPage />
              </ModuleGuard>
            }
          >
            <Route index element={<Navigate to="/configuracion/categorias" replace />} />
            <Route path="categorias" element={<CategoriesPage />} />
            <Route path="marcas" element={<BrandsPage />} />
            <Route path="atributos" element={<AttributesPage />} />
            <Route path="tienda" element={<IdentityPage />} />
            <Route path="comercial" element={<CommercialPage />} />
            <Route path="entrega" element={<DeliveryPage />} />
            <Route path="plan" element={<PlanPage />} />
          </Route>

          {NAV.filter((e) => e.path !== "/" && e.path !== "/configuracion").map((e) => {
            const Screen = SCREENS[e.module];
            return (
              <Route
                key={e.path}
                path={e.path}
                element={
                  <ModuleGuard module={e.module} title={e.label}>
                    {Screen ? <Screen /> : <ComingSoon title={e.label} />}
                  </ModuleGuard>
                }
              />
            );
          })}

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}

/**
 * Sin dashboard contratado, el panel aterriza en Pedidos, que es lo que el
 * usuario abre a diario de todos modos.
 */
function HomeRedirect() {
  const { org } = useActiveOrg();
  const { data, isPending } = useModules(org?.organizationId ?? null);

  if (isPending) return <PageSkeleton />;

  const keys = new Set((data ?? []).map((m) => m.key));
  if (keys.has("dashboard")) return <DashboardPage />;
  if (keys.has("orders")) return <Navigate to="/ventas" replace />;

  const first = NAV.find((e) => keys.has(e.module) && e.path !== "/");
  return first ? <Navigate to={first.path} replace /> : <NoModules />;
}

/**
 * Un modulo que el plan no incluye enseña el candado y lo que hace.
 *
 * Durante casi todo el proyecto esto respondia 404, para no confirmar que la
 * seccion existia. Ese criterio vale para lo que protege datos; aqui no
 * protege nada —el catalogo de planes es publico— y a cambio dejaba al
 * cliente sin poder pedir lo que no sabe que existe.
 *
 * Lo que sigue sin cambiar es quien manda: las funciones del modulo
 * comprueban `app.module_enabled()` por su cuenta. Esta capa decide que se
 * enseña, nunca que se permite.
 *
 * Un modulo que no esta en el catalogo sigue siendo 404: eso si es no existir.
 */
function ModuleGuard({
  module,
  title,
  children,
}: {
  module: string;
  title: string;
  children: ReactNode;
}) {
  const { data, isPending } = useModuleAccess();

  if (isPending) return <PageSkeleton />;

  const acceso = (data ?? []).find((m) => m.key === module);
  if (!acceso) return <NotFound />;
  if (!acceso.contratado) return <LockedModule moduleKey={module} title={title} />;

  return <>{children}</>;
}

/* ------------------------------------------------------------------ estados */

function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center bg-ground">
      <span className="font-mono text-meta uppercase tracking-[0.18em] text-ink-3">
        Synkro
      </span>
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-64 w-full rounded-lg" />
    </div>
  );
}

/**
 * El texto de cada seccion pendiente dice la verdad concreta.
 *
 * El mensaje generico anterior —"esta construida en la base de datos"— era
 * falso para Marketing, que no tiene ni tablas. Un panel que promete lo que no
 * tiene se paga caro cuando el cliente lo descubre.
 */
const PENDIENTES: Record<string, string> = {};

function ComingSoon({ title }: { title: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface">
      <EmptyState
        icon={<Construction className="size-5" />}
        title={title}
        description={
          PENDIENTES[title] ??
          "Esta sección está construida en la base de datos y todavía no tiene pantalla."
        }
      />
    </div>
  );
}

function NotFound() {
  return (
    <div className="rounded-lg border border-line bg-surface">
      <EmptyState
        title="No encontrado"
        description="La página que buscas no existe."
      />
    </div>
  );
}

function NoModules() {
  return (
    <div className="rounded-lg border border-line bg-surface">
      <EmptyState
        icon={<Store className="size-5" />}
        title="Tu tienda aún no tiene módulos activos"
        description="Escríbenos para que activemos tu plan."
      />
    </div>
  );
}
